import { CheckCircle } from "@phosphor-icons/react";

const labelClass = (h) =>
  `text-[10px] uppercase tracking-[0.16em] font-semibold ${h ? "text-brand-copper" : "text-brand-muted"}`;
const lineClass = (h) =>
  `text-sm flex justify-between items-baseline gap-3 ${h ? "text-brand-bone/85" : "text-brand-ink/80"}`;

function TierHeader({ icon: Icon, eyebrow, name, highlight }) {
  return (
    <>
      <div className="flex items-center gap-2.5 mb-4">
        <span
          className={`inline-flex items-center justify-center w-9 h-9 rounded-md ${
            highlight ? "bg-brand-bone/15 text-brand-copper" : "bg-brand-cream text-brand-forest"
          }`}
        >
          <Icon size={18} weight="duotone" />
        </span>
        <span
          className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${
            highlight ? "text-brand-copper" : "text-brand-muted"
          }`}
        >
          {eyebrow}
        </span>
      </div>
      <h3 className={`font-serif text-2xl leading-tight ${highlight ? "text-brand-bone" : "text-brand-forest"}`}>
        {name}
      </h3>
    </>
  );
}

function TierPrice({ price, unit, plus, highlight }) {
  return (
    <>
      <div className="mt-5 flex items-baseline gap-2 flex-wrap">
        <span className={`font-serif text-3xl ${highlight ? "text-brand-bone" : "text-brand-forest"}`}>
          {price}
        </span>
        <span className={`text-sm ${highlight ? "text-brand-bone/70" : "text-brand-ink/60"}`}>
          {unit}
        </span>
      </div>
      {plus && <div className="mt-1 text-xs font-semibold text-brand-copper">{plus}</div>}
    </>
  );
}

function TierCommitment({ commitment, highlight }) {
  if (!commitment) return null;
  return (
    <div className={`mt-6 p-4 rounded-md border ${highlight ? "border-brand-bone/15 bg-brand-bone/5" : "border-brand-line bg-brand-cream/40"}`}>
      <div className={labelClass(highlight) + " mb-3"}>Minimum monthly commitment</div>
      <div className="space-y-1.5">
        <div className={lineClass(highlight)}>
          <span>Platform access</span>
          <span className="font-semibold tabular-nums">{commitment.platform}</span>
        </div>
        <div className={lineClass(highlight)}>
          <span>Prepaid lead credit</span>
          <span className="font-semibold tabular-nums">{commitment.leadCredit}</span>
        </div>
        <div
          className={`flex justify-between items-baseline gap-3 pt-2 mt-2 border-t text-sm font-semibold ${
            highlight ? "border-brand-bone/15 text-brand-copper" : "border-brand-line text-brand-forest"
          }`}
        >
          <span>Total monthly minimum</span>
          <span className="tabular-nums">{commitment.total}</span>
        </div>
      </div>
    </div>
  );
}

function TierLeadPricing({ leadPricing, highlight }) {
  if (!leadPricing) return null;
  return (
    <div className="mt-4">
      <div className={labelClass(highlight) + " mb-2"}>{leadPricing.title || "Lead pricing"}</div>
      <ul className="space-y-1.5 text-xs">
        {leadPricing.items.map((item) => (
          <li key={item} className={`flex gap-2 items-start ${highlight ? "text-brand-bone/80" : "text-brand-ink/75"}`}>
            <span className="text-brand-copper">·</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TierIncluded({ included, highlight }) {
  return (
    <div className="mt-6">
      <div className={labelClass(highlight) + " mb-3"}>Includes</div>
      <ul className="space-y-2 text-sm">
        {included.map((i) => (
          <li key={i} className="flex gap-2 items-start">
            <CheckCircle
              size={14}
              weight="fill"
              className={highlight ? "text-brand-copper mt-1 shrink-0" : "text-brand-forest mt-1 shrink-0"}
            />
            <span className={highlight ? "text-brand-bone/90" : "text-brand-ink/80"}>{i}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TierBestFor({ bestFor, highlight }) {
  if (!bestFor) return null;
  return (
    <div className="mt-6">
      <div className={labelClass(highlight) + " mb-3"}>Best for</div>
      <ul className="space-y-1.5 text-xs">
        {bestFor.map((b) => (
          <li key={b} className={`flex gap-2 items-start ${highlight ? "text-brand-bone/70" : "text-brand-ink/65"}`}>
            <span className="text-brand-copper">·</span>
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TierFootnotes({ important, note, highlight }) {
  return (
    <>
      {important && (
        <p
          className={`mt-5 pt-5 border-t text-xs leading-relaxed ${
            highlight ? "border-brand-bone/15 text-brand-bone/75" : "border-brand-line text-brand-ink/70"
          }`}
        >
          <strong className={highlight ? "text-brand-copper" : "text-brand-forest"}>Important. </strong>
          {important}
        </p>
      )}
      {note && (
        <p
          className={`mt-5 pt-5 border-t text-xs italic leading-relaxed ${
            highlight ? "border-brand-bone/15 text-brand-bone/65" : "border-brand-line text-brand-muted"
          }`}
        >
          {note}
        </p>
      )}
    </>
  );
}

function TierCta({ id, ctaLabel, onCta, loading, highlight }) {
  return (
    <div className="mt-auto pt-7">
      <button
        type="button"
        onClick={onCta}
        disabled={loading}
        data-testid={`btn-pick-${id}`}
        className={`block w-full text-center rounded-md py-3 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-wait ${
          highlight
            ? "bg-brand-copper hover:bg-brand-copperHover text-white"
            : "bg-brand-forest hover:bg-brand-forestHover text-brand-bone"
        }`}
      >
        {loading ? "Opening checkout…" : ctaLabel}
      </button>
    </div>
  );
}

export default function Tier(props) {
  const { id, description, highlight, wide } = props;
  return (
    <div
      data-testid={`tier-${id}`}
      className={`relative rounded-md p-7 border flex flex-col ${
        highlight
          ? "bg-brand-forest text-brand-bone border-brand-forest shadow-xl scale-[1.02]"
          : "bg-white text-brand-ink border-brand-line"
      } ${wide ? "lg:p-9" : ""}`}
    >
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-brand-copper text-white text-[10px] uppercase tracking-[0.16em] font-semibold rounded-full">
          ★ Most popular
        </div>
      )}
      <TierHeader {...props} />
      <TierPrice {...props} />
      <p className={`mt-5 text-sm leading-relaxed ${highlight ? "text-brand-bone/80" : "text-brand-ink/70"}`}>
        {description}
      </p>
      <TierCommitment {...props} />
      <TierLeadPricing {...props} />
      <TierIncluded {...props} />
      <TierBestFor {...props} />
      <TierFootnotes {...props} />
      <TierCta {...props} />
    </div>
  );
}
